import { SupportChipConfig } from "../components/ZendeskChat/types";
import { isSupportDevChipsEnabled } from "@/utils/environment";
import type { SupportEligibility } from "../types";

/**
 * Chip templates for support actions
 */
const CHIP_TEMPLATES: Record<string, SupportChipConfig> = {
  resumeChat: {
    id: "resume-chat",
    label: "Resume Live Chat",
    icon: "commentConvo",
    action: "resumeChat",
    variant: "Button-primary",
    whenToShow: "always",
  },
  chatWithSupport: {
    id: "chat-with-support",
    label: "Chat with Support",
    icon: "commentConvo",
    message: "I need to chat with a support agent",
    variant: "ActionChip-primary",
    whenToShow: "no-conversation",
  },
  screenshareWithSupport: {
    id: "screenshare-with-support",
    label: "Screenshare with Support",
    icon: "miscHeadset",
    message: "I need to screenshare with a support agent",
    variant: "ActionChip-primary",
    whenToShow: "no-conversation",
  },
  resumeConversation: {
    id: "resume-conversation",
    label: "Resume conversation",
    action: "resumeConversation",
    variant: "Button-secondary",
    whenToShow: "loaded-conversation",
    surfaces: ["copilot-input"],
  },
  newConversation: {
    id: "new-conversation",
    label: "New conversation",
    action: "newConversation",
    variant: "Button-tertiary",
    whenToShow: "loaded-conversation",
    surfaces: ["copilot-input"],
    hideWhenActiveLiveChat: true,
  },
  forceChat: {
    id: "force-chat",
    label: "[DEV] Force Chat",
    icon: "commentConvo",
    message:
      "Force initiating live chat - please connect me to a support agent using the supportChat tool with test issue details.",
    variant: "ActionChip-ghost",
    isDevelopmentOnly: true,
    whenToShow: "always",
  },
  forceEmail: {
    id: "force-email",
    label: "[DEV] Force Email",
    icon: "actionEmail",
    message:
      "Force creating an email ticket - please first call get_contact_form_categories to fetch the available categories, then immediately create a support ticket using the showTicketPreview tool with the first available category and test issue details.",
    variant: "ActionChip-ghost",
    isDevelopmentOnly: true,
    whenToShow: "always",
  },
};

export interface BuildSupportChipsOptions {
  /** Support eligibility data for the user */
  supportEligibility: SupportEligibility | null;
  /** Whether the user has an active live chat conversation */
  hasActiveChat?: boolean;
  /** Whether the active chat is in the current copilot conversation */
  isActiveChatInCurrentConversation?: boolean;
  /**
   * Rendering surface. Both surfaces show the same eligibility-based chips.
   * Reserved for future surface-specific layout or sizing differences.
   */
  surface?: "widget" | "fullscreen";
}

/**
 * Builds an array of support chip configurations based on user eligibility.
 *
 * When the user has an active chat in a different conversation, only the
 * "Resume Live Chat" chip is shown. This prevents confusion from showing
 * multiple chat options when one is already in progress.
 *
 * Development chips are automatically included when isSupportDevChipsEnabled
 * is true (controlled via NEXT_PUBLIC_ENABLE_SUPPORT_DEV_CHIPS env var).
 *
 * @example
 * const chips = buildSupportChips({
 *   supportEligibility,
 *   hasActiveChat: true,
 *   isActiveChatInCurrentConversation: false,
 * });
 */
export function buildSupportChips(
  options: BuildSupportChipsOptions,
): SupportChipConfig[] {
  const chips: SupportChipConfig[] = [];
  const { supportEligibility, surface = "fullscreen" } = options;

  // Check if user has an active chat in a different conversation
  const showResumeChat =
    options.hasActiveChat && !options.isActiveChatInCurrentConversation;

  // Resume Chat chip — variant: "important" gives it primary Button prominence
  if (showResumeChat) {
    chips.push(CHIP_TEMPLATES.resumeChat);
  }

  if (supportEligibility?.isChatShortcutEligible && !showResumeChat) {
    chips.push(CHIP_TEMPLATES.chatWithSupport);
  }

  // Screenshare chip
  if (supportEligibility?.isScreenshareEligible) {
    chips.push(CHIP_TEMPLATES.screenshareWithSupport);
  }

  // Conversation navigation chips — only for the fullscreen page (CopilotInput lives there).
  // The widget has no CopilotInput so these chips are never needed there; they are also
  // surface-filtered at render time via their surfaces: ["copilot-input"] config.
  if (surface !== "widget") {
    chips.push(CHIP_TEMPLATES.resumeConversation);
    chips.push(CHIP_TEMPLATES.newConversation);
  }

  // Development-only chips
  if (isSupportDevChipsEnabled) {
    chips.push(CHIP_TEMPLATES.forceChat);
    chips.push(CHIP_TEMPLATES.forceEmail);
  }

  return chips;
}
